import React, { useState, useEffect, FC, PropsWithChildren } from "react";
import { Link } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetOpenTasks } from "@/services/Tasks";
import { AdminNavigation, Navigation, NavigationLimited } from "../Navigation";
import styles from "./styles.module.css";
import Logo from "@/components/atoms/Logo";
import CompanySelect from "../CompanySelect";
import HamburgerNav from "@/components/atoms/HamburgerNav";
import { useAuth } from "@/contexts/AuthContext";
import { Group } from "@mantine/core";

interface BaseHeaderProps {
  variant: "primary" | "secondary" | "simple";
  disableNavigation?: boolean;
}
interface MainHeaderProps {
  disableNavigation?: boolean;
}
const UPDATE_INTERVAL = 60000;

export const BaseHeader: FC<PropsWithChildren<BaseHeaderProps>> = ({ variant, disableNavigation, ...props }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [visible, setVisible] = useState(true);

  return (
    <header className={`${styles.headerRoot} ${styles[variant]}`}>
      <div className={styles.inner}>
        {disableNavigation ? (
          <Logo variant={variant} />
        ) : (
          <Link
            to={variant === "primary" || variant === "simple" ? "/" : "/settings/account"}
            className={styles.logoLink}
          >
            <Logo variant={variant} />
          </Link>
        )}
        {variant !== "simple" && (
          <>
            <HamburgerNav
              variant={variant}
              showCloseButton={isMobileMenuOpen}
              onClick={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen);
                if (visible) {
                  setVisible(false);
                } else {
                  setVisible(true);
                }
              }}
            />
            <div className={`${styles.menu} ${isMobileMenuOpen ? styles.menuOpen : ""}`}>{props.children}</div>
          </>
        )}
      </div>
    </header>
  );
};

export const AdminHeader: FC<PropsWithChildren> = () => {
  return (
    <BaseHeader variant="secondary">
      <AdminNavigation />
    </BaseHeader>
  );
};

export const MainHeader: FC<PropsWithChildren<MainHeaderProps>> = ({ disableNavigation }) => {
  const { signOut, isViewer, currentCompany, changeCompany } = useAuth();
  const [nrOfOpenTasks, setNrOfOpenTasks] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const activePage = location.pathname.split("/")[1];
  const disabledNavigationList = ["registercompany"];
  const isNavigationDisabled = disableNavigation || disabledNavigationList.includes(activePage.toLowerCase());

  const { data: tasksData } = useGetOpenTasks(currentCompany?.companyId as number, undefined, {
    refetchInterval: UPDATE_INTERVAL,
    enabled: !!currentCompany?.companyId,
  });

  useEffect(() => {
    if (tasksData) {
      setNrOfOpenTasks(tasksData.length);
    }
  }, [tasksData]);

  const userOptionsChangedHandler = async (item: "settings" | "logout" | string) => {
    switch (item) {
      case "settings":
        navigate("/settings/account");
        break;
      case "logout":
        signOut();
        break;
      default:
        await changeCompany(Number(item));
    }
  };

  return (
    <BaseHeader variant="primary" disableNavigation={false}>
      {!isNavigationDisabled ? (
        <>
          {!isViewer(currentCompany) ? <Navigation taskCount={nrOfOpenTasks} /> : <NavigationLimited />}
          <CompanySelect onChange={userOptionsChangedHandler} />
        </>
      ) : (
        <Group align={"center"} justify={"end"} w={"100%"}>
          <CompanySelect onChange={userOptionsChangedHandler} />
        </Group>
      )}
    </BaseHeader>
  );
};

export const HeadersContainer: FC<PropsWithChildren> = (props) => {
  return <div className={styles.container}>{props.children}</div>;
};

export const SimpleHeader: FC = () => <BaseHeader variant="simple" disableNavigation={true} />;
